/**
 * usePublishDraft Hook
 * 
 * Provides publish functionality for draft workflows.
 * Publishes the current draft to production with full validation.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useDraftModeStore } from '@/stores/draftModeStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useAppStore } from '@/stores/appStore';
import { useAuth } from '@/auth/useAuth';
import { publishDraft } from '@/services/draftWorkflowApi';
import { cleanWorkflowForSerialization, nonEmptyObject } from '@/services/workflowSerializer';
import { saveNodesMetadata } from '@/services/nodesMetadataApi';
import { commitSidecarPlan, planPublishSidecar } from '@/services/nodesMetadataCache';
import { useWorkflowLayoutStore } from '@/stores/workflowLayoutStore';
import { telemetryService } from '@/services/telemetryService';
import { trackFunnelStep, FunnelNames } from '@/services/telemetryFunnels';
import { isWorkflowEmpty } from '@/utils/validation';
import type { PublishResult } from '@/types/draftMode';

// Variables passed through useMutation so onSuccess/onError receive a stable
// per-call snapshot (no refs, no aliasing across concurrent publishes).
interface PublishVariables {
  wasRepublish: boolean;
}

export interface PublishCallbackInfo {
  /**
   * Whether this publish was a "republish" — i.e. draft == published at click
   * time. Snapshotted before any post-publish store mutations so it always
   * reflects the user's intent at the moment they clicked Publish.
   */
  wasRepublish: boolean;
}

export interface UsePublishDraftOptions {
  /** Workflow name to publish */
  workflowName: string;
  /** Callback on successful publish */
  onSuccess?: (info: PublishCallbackInfo) => void;
  /** Callback on publish error */
  onError?: (error: Error, info: PublishCallbackInfo) => void;
}

export interface UsePublishDraftResult {
  /** Trigger the publish operation */
  publish: () => Promise<PublishResult>;
  /** Whether a publish is in progress */
  isPublishing: boolean;
  /** Last publish error, if any */
  error: string | null;
}

/**
 * Hook to publish the current draft workflow to production
 */
export function usePublishDraft({
  workflowName,
  onSuccess,
  onError,
}: UsePublishDraftOptions): UsePublishDraftResult {
  const queryClient = useQueryClient();
  const { getOttoToken } = useAuth();
  const { selectedProject, selectedApp } = useAppStore();
  
  const [error, setError] = useState<string | null>(null);
  const publishStartTimeRef = useRef<number>(0);

  // Get workflow state
  const workflow = useWorkflowStore((state) => state.workflow);
  const connections = useWorkflowStore((state) => state.connections);
  const markClean = useWorkflowStore((state) => state.markClean);

  // Get draft mode store actions
  const setIsPublishing = useDraftModeStore((state) => state.setIsPublishing);
  const setProdArtifacts = useDraftModeStore((state) => state.setProdArtifacts);
  const discardDraft = useDraftModeStore((state) => state.discardDraft);
  const draftNotes = useDraftModeStore((state) => state.draftNotes);
  const draftParameters = useDraftModeStore((state) => state.draftParameters);

  // Get connections for saving alongside workflow
  const exportConnections = useConnectionStore((state) => state.exportToConnectionsConfig);

  // Mutation for publishing draft workflow
  const mutation = useMutation<PublishResult, Error, PublishVariables>({
    mutationFn: async (): Promise<PublishResult> => {
      publishStartTimeRef.current = performance.now();

      if (!workflow) {
        throw new Error('No workflow to publish');
      }

      if (!selectedProject || !selectedApp) {
        throw new Error('No Logic App selected');
      }

      setIsPublishing(true);

      // Build the workflow definition in the expected format.
      // Clean through the serializer to strip internal-only UI properties
      // (e.g., `detached`) and any legacy position metadata so the runtime
      // gets a clean, layout-free definition. Pixel positions live in the
      // client-side `workflowLayoutStore` instead.
      const cleanedWorkflow = cleanWorkflowForSerialization(workflow, {});
      const workflowNotes = nonEmptyObject(workflow.notes as Record<string, unknown> | undefined);
      const workflowDefinition = {
        definition: cleanedWorkflow.definition,
        kind: workflow.kind || 'Stateful',
        ...(workflowNotes && { notes: workflowNotes }),
      };

      // Get connections config
      const connectionsConfig = exportConnections();

      const result = await publishDraft({
        projectName: selectedProject,
        appName: selectedApp.name,
        getToken: getOttoToken,
        workflowName,
        workflowDefinition,
        connectionsConfig: nonEmptyObject(connectionsConfig),
        parametersConfig: nonEmptyObject(draftParameters),
        notesConfig: nonEmptyObject(draftNotes),
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to publish');
      }

      // Snapshot the just-published layout (draft branch → published branch)
      // and persist the sidecar. This is the moment the published view's
      // layout becomes meaningful — readers viewing the published workflow
      // expect the same arrangement the author just released.
      const identity = useWorkflowStore.getState().currentWorkflowIdentity;
      if (identity) {
        try {
          useWorkflowLayoutStore.getState().snapshotDraftToPublished(identity);
          const plan = planPublishSidecar(workflow, identity);
          if (plan) {
            const ok = await saveNodesMetadata(
              {
                projectName: selectedProject,
                appName: selectedApp.name,
                getToken: getOttoToken,
                workflowName,
              },
              plan.file,
            );
            if (ok) commitSidecarPlan(plan);
          }
        } catch (err) {
          console.warn('[usePublishDraft] Failed to persist nodesMetadata sidecar', err);
        }
      }

      return result;
    },
    onSuccess: (_data, { wasRepublish }) => {
      const duration_ms = performance.now() - publishStartTimeRef.current;

      setError(null);
      setIsPublishing(false);
      
      // Update production artifacts to match current workflow
      if (workflow) {
        setProdArtifacts({
          workflow: workflow,
          connections: connections,
          parameters: draftParameters,
          notes: draftNotes,
        });
      }

      // Reset draft artifacts to match the now-published production state
      discardDraft();

      // Mark workflow as clean
      markClean();

      // Invalidate workflowTriggers cache so TriggerWorkflowDialog and the
      // node editor's HTTP URL field fetch fresh data (callback URLs change
      // after publish). The runtime may not have the published trigger's
      // callback URL ready at the exact moment this mutation resolves, so we
      // schedule a couple of follow-up invalidations to catch the runtime
      // catching up — bounded so we don't poll indefinitely.
      //
      // Only the immediate invalidation is broad (so the run dialog also
      // refreshes); the delayed retries are scoped via predicate to ONLY the
      // per-trigger callback URL queries to avoid re-running the heavier
      // useWorkflowTriggers fan-out (which itself calls /listCallbackUrl once
      // per trigger) on every retry.
      const triggersKey = ['workflowTriggers', workflowName, selectedProject, selectedApp?.name];
      void queryClient.invalidateQueries({ queryKey: triggersKey });

      // The Parameters / Connections pages read the on-disk JSON via React
      // Query, not the draft store. Seed both prod and draft caches with what
      // was just published so observers (designer's useAppLevelParameters,
      // the Parameters/Connections page on next mount) reflect the new values
      // immediately. Using setQueryData (not invalidate) avoids a refetch
      // race with the backend's read-after-write window — we already know the
      // canonical post-publish value: it's what we just sent.
      const projectKey = selectedProject?.toLowerCase();
      const appKey = selectedApp?.name?.toLowerCase();
      const seedProdAndDraft = <T>(baseKey: string, value: T) => {
        queryClient.setQueryData([baseKey, projectKey, appKey], value);
        queryClient.setQueryData([`${baseKey}-draft`, projectKey, appKey], value);
      };
      seedProdAndDraft('parameters', draftParameters ?? {});
      seedProdAndDraft('connections', exportConnections() ?? {});

      const isCallbackUrlQueryFor = (key: readonly unknown[]): boolean =>
        key.length >= 6 &&
        key[0] === 'workflowTriggers' &&
        key[1] === workflowName &&
        key[2] === selectedProject &&
        key[3] === selectedApp?.name &&
        key[4] === 'callback';

      window.setTimeout(() => {
        void queryClient.invalidateQueries({
          predicate: (q) => isCallbackUrlQueryFor(q.queryKey),
        });
      }, 1500);
      window.setTimeout(() => {
        void queryClient.invalidateQueries({
          predicate: (q) => isCallbackUrlQueryFor(q.queryKey),
        });
      }, 4000);

      telemetryService.trackEvent(
        'workflow.draft.published',
        { wasRepublish: String(wasRepublish) },
        { duration_ms },
      );
      trackFunnelStep(FunnelNames.WORKFLOW_DESIGN, 'draft_publish', 5, { duration_ms });

      onSuccess?.({ wasRepublish });
    },
    onError: (err, { wasRepublish }) => {
      console.error('[usePublishDraft] Publish failed:', err);
      setError(err.message);
      setIsPublishing(false);

      telemetryService.trackEvent('workflow.draft.publish_failed', {
        error: err.message,
        wasRepublish: String(wasRepublish),
      });

      onError?.(err, { wasRepublish });
    },
  });

  const publish = useCallback(async (): Promise<PublishResult> => {
    // Snapshot the republish flag at click time so onSuccess/onError see the
    // value the user intended, not the value after setProdArtifacts runs.
    const wasRepublish = useDraftModeStore.getState().areProdAndDraftEqual();

    if (!workflow || !selectedApp || !selectedProject) {
      const errorMsg = !workflow ? 'No workflow to publish' : 'No Logic App selected';
      console.error('[usePublishDraft] Publish pre-condition failed:', errorMsg);
      telemetryService.trackEvent('workflow.draft.publish_precondition_failed', { error: errorMsg });
      onError?.(new Error(errorMsg), { wasRepublish });
      return {
        success: false,
        error: errorMsg,
      };
    }

    // Block publishing an empty workflow (no triggers AND no actions). The
    // runtime would happily accept it, but a workflow with nothing to run
    // can't be invoked and only creates confusing prod state.
    if (isWorkflowEmpty(workflow)) {
      const errorMsg = 'Cannot publish an empty workflow. Add a trigger to get started.';
      console.error('[usePublishDraft] Publish pre-condition failed:', errorMsg);
      telemetryService.trackEvent('workflow.draft.publish_precondition_failed', { error: 'empty_workflow' });
      onError?.(new Error(errorMsg), { wasRepublish });
      return {
        success: false,
        error: errorMsg,
      };
    }

    try {
      return await mutation.mutateAsync({ wasRepublish });
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Unknown error',
      };
    }
  }, [workflow, selectedApp, selectedProject, onError, mutation]);

  return {
    publish,
    isPublishing: mutation.isPending,
    error,
  };
}
